import { useEffect, useState, useRef } from "react";
// eslint-disable-next-line no-unused-vars
const _pf_marker = 0;

function LoopCard({ loop, onDelete }) {
  const [playing, setPlaying] = useState(false);
  const ctxRef = useRef(null);

  const play = () => {
    if (playing) return;
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!ctxRef.current) ctxRef.current = new AC();
    const ctx = ctxRef.current;
    if (ctx.state === "suspended") ctx.resume();
    setPlaying(true);
    const NOTES = [261.63, 293.66, 329.63, 349.23, 392, 440, 493.88, 523.25];
    loop.notes.forEach((n) => {
      setTimeout(() => {
        const o = ctx.createOscillator();
        const g = ctx.createGain();
        o.type = "sawtooth";
        o.frequency.value = NOTES[n.idx] || 440;
        const f = ctx.createBiquadFilter();
        f.type = "lowpass"; f.frequency.value = 1400;
        o.connect(f); f.connect(g); g.connect(ctx.destination);
        const t = ctx.currentTime;
        g.gain.setValueAtTime(0, t);
        g.gain.linearRampToValueAtTime(0.15, t + 0.01);
        g.gain.exponentialRampToValueAtTime(0.0001, t + 0.35);
        o.start(t); o.stop(t + 0.4);
      }, n.t);
    });
    setTimeout(() => setPlaying(false), 4300);
    // record play
    import("@/lib/api").then((m) => m.playedLoop(loop.id).catch(() => {}));
  };

  const share = () => {
    const url = `${window.location.origin}/app/jixxeo-vibes?loop=${loop.id}`;
    if (navigator.share) navigator.share({ title: `Jixxeo loop: ${loop.name}`, url }).catch(() => {});
    else { navigator.clipboard.writeText(url); toast.success("Loop link copied"); }
  };

  return (
    <div className="glass rounded-2xl p-4 flex items-center gap-3 relative overflow-hidden">
      {/* mini bar-viz */}
      <div className="w-14 h-14 rounded-lg bg-black/50 border border-[#00F0FF]/30 flex items-center justify-center p-1.5">
        <div className="flex items-end gap-0.5 h-full w-full">
          {Array.from({ length: 8 }).map((_, i) => {
            const hits = loop.notes.filter((n) => n.idx === i).length;
            const h = 10 + Math.min(90, hits * 30);
            return <div key={i} className="flex-1 rounded-sm" style={{ background: "#00F0FF", height: `${h}%`, opacity: 0.4 + Math.min(0.6, hits * 0.2) }} />;
          })}
        </div>
      </div>
      <div className="min-w-0 flex-1">
        <div className="font-heading font-bold truncate">{loop.name}</div>
        <div className="mono text-[10px] text-white/50">{loop.notes.length} notes · {loop.bpm} BPM · {loop.plays || 0} plays</div>
      </div>
      <button onClick={play}
        className="w-10 h-10 rounded-full bg-[#00F0FF] hover:bg-white text-black flex items-center justify-center flex-shrink-0" title="Play">
        <Play className="w-4 h-4" fill={playing ? "#000" : "none"} />
      </button>
      <button onClick={share} className="w-9 h-9 rounded-full border border-white/10 hover:border-[#39FF14]/50 hover:text-[#39FF14] flex items-center justify-center">
        <Share2 className="w-4 h-4" />
      </button>
      <button onClick={onDelete} className="w-9 h-9 rounded-full border border-white/10 hover:border-[#FF003C]/50 hover:text-[#FF003C] flex items-center justify-center">
        <Trash2 className="w-4 h-4" />
      </button>
    </div>
  );
}


import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Coins, ShieldCheck, Rocket, Heart, Calendar, XCircle, Music, Play, Share2, Trash2, Flame, Palette, LayoutDashboard } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { startCheckout, usePaymentReturn } from "@/lib/checkout";
import { myLibrary, myTips, myLoops, deleteLoop, devToggleAutoRenew, devCancelSub, formatApiError, formatDownloads, streakStatus, myArtworks, deleteArtwork } from "@/lib/api";
import { AppCard } from "@/components/AppCard";
import Footer from "@/components/Footer";
import { PushPreferences } from "@/components/PushPanel";
import { PROFILE } from "@/constants/testIds";

export default function Profile() {
  const { user, refresh } = useAuth();
  const [lib, setLib] = useState([]);
  const [tips, setTips] = useState([]);
  const [loops, setLoops] = useState([]);
  const [streak, setStreak] = useState(null);
  const [artworks, setArtworks] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    myLibrary().then((d) => setLib(d.saved || []));
    myTips().then((d) => setTips(d.tips || []));
    myLoops().then((d) => setLoops(d.loops || [])).catch(() => {});
    streakStatus().then(setStreak).catch(() => {});
    myArtworks().then((d) => setArtworks(d.artworks || [])).catch(() => {});
  }, []);

  const upgradeDev = async () => {
    setBusy(true);
    await startCheckout("jx_dev_year");
    setBusy(false);
  };
  usePaymentReturn(refresh);

  if (!user) return null;

  return (
    <div data-testid={PROFILE.root}>
      <div className="max-w-6xl mx-auto px-6 pt-10">
        <div className="flex flex-col md:flex-row items-start gap-6">
          <div className="w-24 h-24 md:w-32 md:h-32 rounded-3xl flex items-center justify-center font-heading font-black text-5xl ring-2"
            style={{ background: `${user.avatar_color || "#00F0FF"}22`, color: user.avatar_color || "#00F0FF", borderColor: `${user.avatar_color}55` }}>
            {(user.name || user.email)[0].toUpperCase()}
          </div>
          <div className="flex-1">
            <div className="mono text-[10px] uppercase tracking-[0.3em] text-[#00F0FF] mb-2">// member profile</div>
            <h1 className="font-heading font-black text-4xl sm:text-5xl uppercase tracking-tighter">{user.name}</h1>
            <div className="text-white/60 mt-1">{user.email}</div>
            <div className="flex flex-wrap items-center gap-3 mt-3">
              <span className="inline-flex items-center gap-1.5 mono text-xs px-3 py-1.5 rounded-full border border-[#FFE300]/30 bg-[#FFE300]/10 text-[#FFE300]">
                <Coins className="w-3.5 h-3.5" /> {user.coins} coins
              </span>
              {user.is_developer && (
                <span className="inline-flex items-center gap-1.5 mono text-xs px-3 py-1.5 rounded-full border border-[#00F0FF]/40 bg-[#00F0FF]/10 text-[#00F0FF]">
                  <ShieldCheck className="w-3.5 h-3.5" /> Verified Developer
                </span>
              )}
              {user.role === "admin" && (
                <span className="mono text-xs px-3 py-1.5 rounded-full border border-[#FF003C]/40 bg-[#FF003C]/10 text-[#FF003C]">Admin</span>
              )}
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {user.role === "admin" && (
              <Link to="/admin" data-testid="profile-admin-dashboard-btn" className="inline-flex items-center gap-2 bg-[#FF003C] hover:bg-white hover:text-black text-white font-bold mono text-xs uppercase tracking-widest px-5 py-2.5 rounded-full transition-colors">
                <LayoutDashboard className="w-4 h-4" /> Admin Dashboard
              </Link>
            )}
            <Link to="/coins" className="inline-flex items-center gap-2 bg-[#FFE300] hover:bg-white text-black font-bold mono text-xs uppercase tracking-widest px-5 py-2.5 rounded-full transition-colors">
              <Coins className="w-4 h-4" /> Buy Coins
            </Link>
          </div>
        </div>

        {/* Streak card */}
        <StreakCard streak={streak} />

        {/* Push notifications */}
        <div className="mt-8">
          <PushPreferences />
        </div>

        {/* Developer subscription */}
        {!user.is_developer ? (
          <div className="mt-8 glass rounded-3xl p-6 md:p-8 border border-[#00F0FF]/20 grid-bg relative overflow-hidden">
            <div className="relative flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
              <div>
                <div className="mono text-[10px] uppercase tracking-[0.3em] text-[#00F0FF] mb-2">// go creator</div>
                <h3 className="font-heading font-black text-3xl uppercase tracking-tighter">Become a Creator</h3>
                <p className="text-white/60 mt-2 max-w-xl">
                  Publish apps, get tipped, view analytics. <strong className="text-[#00F0FF]">$20/year</strong> — auto-renews.
                </p>
              </div>
              <button data-testid={PROFILE.subscribeBtn} onClick={upgradeDev} disabled={busy}
                className="inline-flex items-center gap-2 bg-[#00F0FF] hover:bg-white text-black font-bold mono text-xs uppercase tracking-widest px-6 py-3 rounded-full pulse-neon">
                <Rocket className="w-4 h-4" /> {busy ? "Activating…" : "Activate · $20/yr"}
              </button>
            </div>
          </div>
        ) : (
          <SubscriptionCard user={user} refresh={refresh} />
        )}

        {/* Library */}
        <section data-testid={PROFILE.library} className="mt-12">
          <div className="flex items-center gap-2 mb-4">
            <Heart className="w-5 h-5 text-[#FF003C]" />
            <h2 className="font-heading font-black text-2xl uppercase tracking-tighter">Your Library · {lib.length}</h2>
          </div>
          {lib.length === 0 ? (
            <div className="glass rounded-2xl p-8 text-center">
              <p className="text-white/60">You haven't saved any apps yet. Head to the <Link className="text-[#00F0FF]" to="/feed">Feed</Link> and swipe up ↑</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {lib.map((a) => <AppCard key={a.slug} app={a} />)}
            </div>
          )}
        </section>

        {/* Tips */}
        <section className="mt-12">
          <div className="flex items-center gap-2 mb-4">
            <Palette className="w-5 h-5 text-[#39FF14]" />
            <h2 className="font-heading font-black text-2xl uppercase tracking-tighter">Your Gallery · {artworks.length}</h2>
          </div>
          {artworks.length === 0 ? (
            <div className="glass rounded-2xl p-8 text-center text-white/60 text-sm">
              No artworks yet. Open <Link className="text-[#39FF14]" to="/app/jixxeo-draw">Jixxeo Draw</Link>, sketch something, and hit the upload icon to save to your gallery.
            </div>
          ) : (
            <div data-testid={PROFILE.gallery} className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {artworks.map((a) => (
                <div key={a.id} className="glass rounded-2xl overflow-hidden relative group border border-white/10">
                  <div className="aspect-square bg-black flex items-center justify-center overflow-hidden">
                    <img src={a.image_data_url} alt={a.title} className="w-full h-full object-cover" />
                  </div>
                  <div className="p-3">
                    <div className="font-heading font-bold text-sm truncate">{a.title}</div>
                    <div className="mono text-[9px] text-white/50">{new Date(a.created_at).toLocaleDateString()} · {a.likes || 0} likes</div>
                  </div>
                  <button
                    onClick={async () => {
                      if (!window.confirm(`Delete "${a.title}"?`)) return;
                      await deleteArtwork(a.id);
                      setArtworks((prev) => prev.filter((x) => x.id !== a.id));
                      toast.success("Artwork deleted");
                    }}
                    className="absolute top-2 right-2 w-8 h-8 rounded-full bg-black/60 border border-white/10 text-white/70 hover:text-[#FF003C] hover:border-[#FF003C]/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Loops */}
        <section data-testid={PROFILE.loops} className="mt-12">
          <div className="flex items-center gap-2 mb-4">
            <Music className="w-5 h-5 text-[#00F0FF]" />
            <h2 className="font-heading font-black text-2xl uppercase tracking-tighter">Saved Loops · {loops.length}</h2>
            <Link to="/loops" className="ml-auto mono text-[10px] uppercase tracking-widest text-[#00F0FF] hover:text-white">See loop wall →</Link>
          </div>
          {loops.length === 0 ? (
            <div className="glass rounded-2xl p-8 text-center text-white/60 text-sm">
              No loops yet. Open <Link className="text-[#00F0FF]" to="/app/jixxeo-vibes">Jixxeo Vibes</Link>, record a 4-second loop, and hit Save.
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {loops.map((l) => (
                <LoopCard key={l.id} loop={l} onDelete={async () => {
                  if (!window.confirm(`Delete "${l.name}"?`)) return;
                  await deleteLoop(l.id);
                  setLoops((prev) => prev.filter((x) => x.id !== l.id));
                  toast.success("Loop deleted");
                }} />
              ))}
            </div>
          )}
        </section>

        <section data-testid={PROFILE.tips} className="mt-12 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <Coins className="w-5 h-5 text-[#FFE300]" />
            <h2 className="font-heading font-black text-2xl uppercase tracking-tighter">Tips Sent · {tips.length}</h2>
          </div>
          {tips.length === 0 ? (
            <div className="glass rounded-2xl p-8 text-center">
              <p className="text-white/60">No tips yet. Show a dev some love in the feed.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {tips.map((t) => (
                <div key={t.id} className="glass rounded-xl p-4 flex items-center gap-4">
                  <div className="w-10 h-10 rounded-full bg-[#FFE300]/20 border border-[#FFE300]/40 flex items-center justify-center">
                    <Coins className="w-4 h-4 text-[#FFE300]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-heading font-bold text-sm">To {t.to_developer_name}</div>
                    <div className="mono text-[10px] text-white/50">{t.app_slug} · {new Date(t.created_at).toLocaleDateString()}</div>
                    {t.message && <div className="text-white/70 text-sm mt-1">"{t.message}"</div>}
                  </div>
                  <div className="mono text-lg font-bold text-[#FFE300]">-{t.coins}</div>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
      <Footer />
    </div>
  );
}

function StreakCard({ streak }) {
  if (!streak) return null;
  const cur = streak.current_streak || 0;
  const longest = streak.longest_streak || 0;
  const next = streak.next_reward || 5;
  const done = streak.checked_in_today;
  const days = Array.from({ length: 7 }).map((_, i) => i + 1);
  const REWARDS = [5, 10, 15, 20, 25, 35, 50];
  return (
    <div data-testid={PROFILE.streakCard} className="mt-8 glass rounded-3xl p-6 md:p-8 border border-[#FF8C00]/25 grid-bg relative overflow-hidden">
      <div className="relative flex flex-col lg:flex-row items-start lg:items-center gap-6">
        <div className="flex items-center gap-4">
          <span className="w-14 h-14 rounded-2xl flex items-center justify-center bg-[#FF8C00]/15 border border-[#FF8C00]/40 text-[#FF8C00]">
            <Flame className="w-7 h-7" />
          </span>
          <div>
            <div className="mono text-[10px] uppercase tracking-[0.3em] text-[#FF8C00] mb-1">// launch streak</div>
            <div className="flex items-baseline gap-2">
              <div className="font-heading font-black text-5xl leading-none text-white">{cur}</div>
              <div className="mono text-xs text-white/60">{cur === 1 ? "day" : "days"}</div>
            </div>
            <div className="mono text-[10px] text-white/50 mt-1">Longest: {longest} · Next: +{next} coins</div>
          </div>
        </div>
        <div className="flex-1 grid grid-cols-7 gap-2 w-full">
          {days.map((d) => {
            const active = d <= cur;
            const isToday = d === cur && done;
            return (
              <div key={d} className={`rounded-xl p-2 text-center border transition-colors ${active ? "bg-[#FF8C00]/15 border-[#FF8C00]/50 text-[#FF8C00]" : "bg-white/[0.02] border-white/8 text-white/40"}`}>
                <div className="mono text-[9px] uppercase tracking-widest">Day {d}{d === 7 ? "+" : ""}</div>
                <div className="font-heading font-black text-lg leading-none mt-0.5">+{REWARDS[d - 1]}</div>
                {active && <Flame className="w-3 h-3 mx-auto mt-1" fill={isToday ? "#FF8C00" : "none"} />}
              </div>
            );
          })}
        </div>
        <div className="mono text-[10px] uppercase tracking-widest px-3 py-2 rounded-full border"
          style={{ borderColor: done ? "#39FF1466" : "#FF8C0055", color: done ? "#39FF14" : "#FF8C00" }}>
          {done ? "✓ Claimed today" : "Launch any PWA to claim"}
        </div>
      </div>
    </div>
  );
}

function SubscriptionCard({ user, refresh }) {  const expires = user.dev_sub_expires_at ? new Date(user.dev_sub_expires_at) : null;
  const auto = user.dev_sub_auto_renew !== false;
  const doToggle = async () => {
    try {
      await devToggleAutoRenew(!auto);
      await refresh();
      toast.success(!auto ? "Auto-renew turned on" : "Auto-renew turned off");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const doCancel = async () => {
    if (!window.confirm("Cancel auto-renew? Your access continues until it expires.")) return;
    try {
      await devCancelSub();
      await refresh();
      toast.success("Auto-renew cancelled");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div className="mt-8 glass rounded-3xl p-6 md:p-8 border border-[#00F0FF]/25 grid-bg relative overflow-hidden">
      <div className="relative flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <div>
          <div className="mono text-[10px] uppercase tracking-[0.3em] text-[#00F0FF] mb-2 flex items-center gap-2">
            <Rocket className="w-3 h-3" /> // creator plan · active
          </div>
          <h3 className="font-heading font-black text-3xl uppercase tracking-tighter">Creator Subscription</h3>
          <div className="flex flex-wrap items-center gap-3 mt-3 text-sm text-white/70">
            <span className="mono text-xs px-2 py-1 rounded-full border border-[#39FF14]/40 bg-[#39FF14]/10 text-[#39FF14]">$20/year</span>
            {expires && (
              <span className="inline-flex items-center gap-1"><Calendar className="w-3.5 h-3.5 text-white/50" /> Renews {expires.toLocaleDateString()}</span>
            )}
            <span className={`mono text-[10px] uppercase px-2 py-0.5 rounded-full border ${auto ? "border-[#00F0FF]/40 text-[#00F0FF]" : "border-white/20 text-white/60"}`}>
              Auto-renew · {auto ? "ON" : "OFF"}
            </span>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Link to="/dev/publish" className="inline-flex items-center gap-2 bg-[#00F0FF] hover:bg-white text-black font-bold mono text-xs uppercase tracking-widest px-4 py-2.5 rounded-full">
            <Rocket className="w-3.5 h-3.5" /> Publish
          </Link>
          <Link to="/dev/apps" className="mono text-xs uppercase tracking-widest text-white/70 hover:text-white px-3 py-2.5 border border-white/15 rounded-full">
            Dashboard
          </Link>
          <button onClick={doToggle} className="mono text-[10px] uppercase tracking-widest text-white/70 hover:text-[#00F0FF] px-3 py-2 border border-white/15 rounded-full">
            {auto ? "Turn off auto-renew" : "Turn on auto-renew"}
          </button>
          {auto && (
            <button onClick={doCancel} className="mono text-[10px] uppercase tracking-widest text-[#FF003C] hover:text-white flex items-center gap-1 px-3 py-2 border border-[#FF003C]/40 rounded-full">
              <XCircle className="w-3 h-3" /> Cancel
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
